import * as M from '@mantine/core';
import { handleChangeLocaleTime } from '../../../../../../../shared/utils/time';
import HoverCardText from '../../../../../../ui/hoverCardText';
import type { ManualUploadStatus } from '../../../../../../../shared/apis/ai-agent/manual/types';
import { IconDotsVertical, IconHierarchy, IconHistory, IconTrash } from '@tabler/icons-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import UsageCard from './usageCard';
import Swal from 'sweetalert2';
import { buildSearchString } from '../../../utils/buildSearchString';
import { useGetAiAgentLearningUsage } from '../../../../../../../shared/apis/ai-agent/manual/reactQueries';
import useDeleteManual from '../hooks/useDeleteManuals';
import usePutFileApplied from '../hooks/usePutFileApplied';
import { ApiResponse } from '../../../../../../../shared/types/response/types';
import { IManual } from '../../../../../../../shared/apis/ai-agent/manual/types';

interface ManualListTableProps {
  servicesEnabled: {
    scautr: boolean;
    progix: boolean;
    prodoq: boolean;
  };
  openMappingModal: (fileUploadId: number) => void;
  openFileUploadModal: () => void;
  data?: ApiResponse<IManual>;
  page: number;
  size: number;
  onPageChange: (page: number) => void;
}

export default function ManualListTable(props: ManualListTableProps) {
  const { data, page, size, onPageChange, openMappingModal, openFileUploadModal, servicesEnabled } = props;
  const manuals = data?.result || [];
  const totalCount = data?.count || 0;
  const theme = M.useMantineTheme();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get('companyName') || '';
  const renderStatusBadge = (status: ManualUploadStatus) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <M.Badge color="green" variant="light">
            완료
          </M.Badge>
        );
      case 'IN_PROGRESS':
        return (
          <M.Badge color="blue" variant="light">
            진행중
          </M.Badge>
        );
      case 'FAILED':
        return (
          <M.Badge color="red" variant="light">
            실패
          </M.Badge>
        );
      default:
        return (
          <M.Badge color="gray" variant="light">
            대기
          </M.Badge>
        );
    }
  };

  const { data: usage, refetch: refetchUsage } = useGetAiAgentLearningUsage({ companyId: Number(companyId) });
  const { mutate: deleteManual } = useDeleteManual();
  const { mutate: putFileApplied } = usePutFileApplied();

  const onClickDelete = (fileUploadId: number) => {
    Swal.fire({
      title: '삭제 전 확인 필요',
      html: '삭제된 파일은 AI 학습 데이터에서 제거되며, <br/> 학습데이터 및 사용량을 복구할 수 없습니다.<br/> 해당 내용에 대한 재학습이 필요할 수 있습니다.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      confirmButtonColor: theme.colors.secondary[5],
    }).then((result) => {
      if (result.isConfirmed) {
        deleteManual({ companyId: Number(companyId), fileUploadId });
      }
    });
  };

  const onClickFileUploadButton = async () => {
    const { data: latestUsage, isError } = await refetchUsage();

    if (isError || !latestUsage) return;

    if (latestUsage.usageTotal >= latestUsage.pageLimit) {
      await Swal.fire({
        title: '파일 업로드 불가',
        text: '학습 페이지 한도를 초과하여 파일을 업로드할 수 없습니다.',
        icon: 'warning',
        confirmButtonText: '확인',
      });
      return;
    }

    openFileUploadModal();
  };

  return (
    <M.Box>
      <M.Group justify="space-between" mb="md" align="flex-end">
        <M.Text fw={600}>매뉴얼 목록</M.Text>
        <M.Group>
          <UsageCard label="AI 학습 사용량" value={usage?.usageTotal || 0} total={usage?.pageLimit || 0} />
          <M.Button size="sm" color="secondary" onClick={() => void onClickFileUploadButton()}>
            파일 업로드
          </M.Button>
        </M.Group>
      </M.Group>
      <M.Table.ScrollContainer minWidth={1200}>
        <M.Table>
          <M.Table.Thead>
            <M.Table.Tr>
              <M.Table.Th>번호</M.Table.Th>
              <M.Table.Th>등록 서비스</M.Table.Th>
              <M.Table.Th>등록 담당자</M.Table.Th>
              <M.Table.Th>매뉴얼명</M.Table.Th>
              <M.Table.Th>학습 페이지 수</M.Table.Th>
              <M.Table.Th>연동 기계모델 수</M.Table.Th>
              <M.Table.Th>SCAUTR 파일 적용</M.Table.Th>
              <M.Table.Th>PROGIX 파일 적용</M.Table.Th>
              <M.Table.Th>PRO-DOQ 파일 적용</M.Table.Th>
              <M.Table.Th>진행 상태</M.Table.Th>
              <M.Table.Th>최종 수정일시</M.Table.Th>
              <M.Table.Th>관리</M.Table.Th>
            </M.Table.Tr>
          </M.Table.Thead>
          <M.Table.Tbody>
            {manuals.length > 0 ? (
              manuals.map((item) => (
                <M.Table.Tr
                  key={item.id}
                  style={(theme) => ({
                    cursor: 'pointer',
                    color: item.deleteDt ? theme.colors.gray[5] : 'inherit',
                  })}
                  onClick={() => {
                    navigate(
                      `/admin/management/aiagent/company/${companyId}/manual/${item.id}/status${buildSearchString({
                        companyName,
                        fileName: item.fileName,
                      })}`,
                    );
                  }}
                >
                  <M.Table.Td>{item.id}</M.Table.Td>
                  <M.Table.Td ta="left">{item.regServiceChannel}</M.Table.Td>
                  <M.Table.Td ta="left">{item.regId}</M.Table.Td>
                  <M.Table.Td ta="left" maw={200}>
                    <HoverCardText value={item.fileName} />
                  </M.Table.Td>
                  <M.Table.Td>{item.usageTotal}</M.Table.Td>
                  <M.Table.Td>{item.modelCount}</M.Table.Td>
                  <M.Table.Td onClick={(event) => event.stopPropagation()}>
                    <M.Flex justify="center">
                      <M.Switch
                        disabled={!servicesEnabled.scautr || !!item.deleteDt || item.taskStatus !== 'COMPLETED'}
                        checked={item.scautrUseYn === 'Y'}
                        onChange={() => {
                          const appliedValue = item.scautrUseYn === 'Y' ? 'N' : 'Y';
                          putFileApplied({ fileUploadId: item.id, scautrUseYn: appliedValue });
                        }}
                      />
                    </M.Flex>
                  </M.Table.Td>
                  <M.Table.Td onClick={(event) => event.stopPropagation()}>
                    <M.Flex justify="center">
                      <M.Switch
                        disabled={!servicesEnabled.progix || !!item.deleteDt || item.taskStatus !== 'COMPLETED'}
                        checked={item.progixUseYn === 'Y'}
                        onChange={() => {
                          const appliedValue = item.progixUseYn === 'Y' ? 'N' : 'Y';
                          putFileApplied({ fileUploadId: item.id, progixUseYn: appliedValue });
                        }}
                      />
                    </M.Flex>
                  </M.Table.Td>
                  <M.Table.Td onClick={(event) => event.stopPropagation()}>
                    <M.Flex justify="center">
                      <M.Switch
                        disabled={!servicesEnabled.prodoq || !!item.deleteDt || item.taskStatus !== 'COMPLETED'}
                        checked={item.useYn === 'Y'}
                        onChange={() => {
                          const appliedValue = item.useYn === 'Y' ? 'N' : 'Y';
                          putFileApplied({ fileUploadId: item.id, useYn: appliedValue });
                        }}
                      />
                    </M.Flex>
                  </M.Table.Td>
                  <M.Table.Td>{renderStatusBadge(item.taskStatus)}</M.Table.Td>
                  <M.Table.Td>{handleChangeLocaleTime(item.modDt)}</M.Table.Td>
                  <M.Table.Td>
                    <M.Menu shadow="md" withArrow width={120} position="bottom-end" offset={4} withinPortal>
                      <M.Menu.Target>
                        <M.ActionIcon variant="default" onClick={(event: any) => event.stopPropagation()}>
                          <IconDotsVertical size={16} />
                        </M.ActionIcon>
                      </M.Menu.Target>
                      <M.Menu.Dropdown onClick={(event) => event.stopPropagation()}>
                        <M.Menu.Item
                          disabled={!!item.deleteDt}
                          leftSection={<IconHierarchy size={14} />}
                          onClick={() => openMappingModal(item.id)}
                        >
                          기계모델 연동
                        </M.Menu.Item>
                        <M.Menu.Item
                          leftSection={<IconHistory size={14} />}
                          onClick={() =>
                            navigate(
                              `/admin/management/aiagent/company/${companyId}/manual/${item.id}/history${buildSearchString({
                                companyName,
                                manualName: item.fileName,
                              })}`,
                            )
                          }
                        >
                          이력 조회
                        </M.Menu.Item>
                        <M.Menu.Item
                          disabled={!!item.deleteDt}
                          leftSection={<IconTrash size={14} />}
                          onClick={() => onClickDelete(item.id)}
                        >
                          삭제
                        </M.Menu.Item>
                      </M.Menu.Dropdown>
                    </M.Menu>
                  </M.Table.Td>
                </M.Table.Tr>
              ))
            ) : (
              <M.Table.Tr>
                <M.Table.Td colSpan={12}>
                  <M.Text ta="center" c="gray.6" size="sm">
                    등록된 매뉴얼이 없습니다.
                  </M.Text>
                </M.Table.Td>
              </M.Table.Tr>
            )}
          </M.Table.Tbody>
        </M.Table>
      </M.Table.ScrollContainer>
      <M.Group mt="md" justify="center">
        <M.Pagination total={Math.ceil((totalCount ?? 0) / size)} value={page} onChange={onPageChange} />
      </M.Group>
    </M.Box>
  );
}
